// src/registry/fetch-template-models.ts — test connection and list models for template providers

import { TEST_TIMEOUT_MS } from '../constants.js';
import { OPENCODE_GO_PROVIDER_ID } from '../data/opencode-go-models.js';
import { deriveBrand } from '../models.js';
import { resolveContextWindow } from '../context-window.js';
import type { ProviderTemplate } from '../provider-templates.js';
import { normalizeGoogleDisplayName, normalizeGoogleModelId } from './google-model-id.js';
import type { CachedModel } from './types.js';
import { openCodeGoPinnedApiUrl } from './resolve-template.js';
import { hasControlChars } from './server-text.js';
import {
  getProviderDebugLogPath,
  makeTraceLogger,
  registerTraceSecret,
} from '../trace-log.js';
import { classifyFreeStatus, isFreeStatus } from '../free-models.js';


type OpenAiModelListResponse = ProviderModelListRow[] | {
  data?: ProviderModelListRow[];
  models?: ProviderModelListRow[];
};

interface ProviderModelListRow {
  id?: string;
  name?: string;
  supported_parameters?: string[];
  context_length?: number;
  contextWindow?: number;
  context_window?: number;
  /** vLLM's spelling, in each OpenAI `ModelCard` it serves. */
  max_model_len?: number;
  isFree?: boolean;
  pricing?: Record<string, string | number | undefined>;
  use_responses_lite?: boolean;
  prefer_websockets?: boolean;
}

function modelFormatForNpm(npm: string): 'anthropic' | 'openai' {
  return npm === '@ai-sdk/anthropic' ? 'anthropic' : 'openai';
}

function modelsUrl(baseUrl: string, template: ProviderTemplate): string {
  const trimmed = baseUrl.replace(/\/$/, '');
  if (template.modelsPath) {
    const path = template.modelsPath.startsWith('/') ? template.modelsPath : `/${template.modelsPath}`;
    return `${trimmed}${path}`;
  }
  
  // Note: the 'openai' token matches path segments like /v1/openai (DeepInfra
  // pattern) and custom proxies like /proxy/openai — both get /models appended
  // directly, not /v1/models. This is the intended heuristic.
  if (/\/(v\d+[a-z]*|openai|beta)$/.test(trimmed)) {
    return `${trimmed}/models`;
  }
  return `${trimmed}/v1/models`;
}

function toNumber(value: string | number | undefined): number | undefined {
  if (value === undefined) return undefined;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function perMillion(value: number | undefined): number | undefined {
  if (value === undefined) return undefined;
  return Number((value * 1_000_000).toPrecision(12));
}

function parseNativePricing(pricing: ProviderModelListRow['pricing']): CachedModel['cost'] | undefined {
  if (!pricing) return undefined;

  const inputPerToken =
    toNumber(pricing.prompt) ??
    toNumber(pricing.input) ??
    toNumber(pricing.input_cost_per_token) ??
    toNumber(pricing.inputCostPerToken);
  const outputPerToken =
    toNumber(pricing.completion) ??
    toNumber(pricing.output) ??
    toNumber(pricing.output_cost_per_token) ??
    toNumber(pricing.outputCostPerToken);

  const inputPerMillion =
    toNumber(pricing.input_per_1m_tokens) ??
    toNumber(pricing.inputPer1MTokens);
  const outputPerMillion =
    toNumber(pricing.output_per_1m_tokens) ??
    toNumber(pricing.outputPer1MTokens);

  const input = perMillion(inputPerToken) ?? inputPerMillion;
  const output = perMillion(outputPerToken) ?? outputPerMillion;
  if (input === undefined && output === undefined) return undefined;

  const cost: CachedModel['cost'] = {
    input: input ?? 0,
    output: output ?? 0,
  };

  const cacheRead = perMillion(toNumber(pricing.input_cache_read) ?? toNumber(pricing.cache_read));
  const cacheWrite = perMillion(toNumber(pricing.input_cache_write) ?? toNumber(pricing.cache_write));
  if (cacheRead !== undefined) cost.cache_read = cacheRead;
  if (cacheWrite !== undefined) cost.cache_write = cacheWrite;

  return cost;
}

function parseModelList(
  body: OpenAiModelListResponse,
  npm: string,
  allowBareArray: boolean,
): CachedModel[] {
  // A bare top-level array is OpenCode Go's documented `/models` shape, and
  // only its call site opts in. Every other template was reviewed against the
  // object envelope, so an upstream that starts answering with an array of
  // something else keeps reporting "no models" instead of turning unreviewed
  // rows into catalog entries.
  const rows = Array.isArray(body)
    ? (allowBareArray ? body : [])
    : body.data ?? body.models ?? [];
  const format = modelFormatForNpm(npm);
  const models: CachedModel[] = [];

  for (const row of rows) {
    const rawId = row.id?.trim();
    if (!rawId) continue;
    // The server chose these strings and the model picker prints them on every
    // later run, so an entry a terminal could act on is not kept. Both are
    // checked trimmed, as they are stored: a name that only ends in a newline
    // is an ordinary model, not a hostile one.
    if (hasControlChars(rawId) || (typeof row.name === 'string' && hasControlChars(row.name.trim()))) continue;
    const { id, upstreamModelId } = normalizeGoogleModelId(rawId, npm);
    const family = id.split(/[-/:]/)[0] ?? id;
    const cost = parseNativePricing(row.pricing);
    const freeStatus = classifyFreeStatus({
      model: { cost, isFree: row.isFree },
    });
    const contextWindow =
      row.context_length ??
      row.contextWindow ??
      row.context_window ??
      row.max_model_len ??
      resolveContextWindow(id);
    models.push({
      id,
      name: normalizeGoogleDisplayName(row.name, id),
      upstreamModelId,
      family,
      brand: deriveBrand(family),
      contextWindow,
      cost,
      isFree: isFreeStatus(freeStatus),
      freeStatus,
      modelFormat: format,
      npm,
      supportedParameters: Array.isArray(row.supported_parameters) ? row.supported_parameters : undefined,
      useResponsesLite: typeof row.use_responses_lite === 'boolean' ? row.use_responses_lite : undefined,
      preferWebSockets: typeof row.prefer_websockets === 'boolean' ? row.prefer_websockets : undefined,
    });
  }

  return models;
}

function materializeTemplateModel(
  template: ProviderTemplate,
  model: NonNullable<ProviderTemplate['staticModels']>[number],
  baseUrl: string,
): CachedModel {
  const npm = model.npm ?? template.npm;
  const { id, upstreamModelId: normalizedUpstream } = normalizeGoogleModelId(model.id, npm);
  const family = model.family ?? (id.split(/[-/:]/)[0] ?? id);
  const freeStatus = model.freeStatus ?? classifyFreeStatus({ model });

  return {
    ...model,
    id,
    name: normalizeGoogleDisplayName(model.name, id),
    upstreamModelId: model.upstreamModelId ?? normalizedUpstream,
    family,
    brand: model.brand ?? deriveBrand(family),
    contextWindow: model.contextWindow ?? resolveContextWindow(id),
    isFree: model.isFree ?? isFreeStatus(freeStatus),
    freeStatus,
    modelFormat: model.modelFormat ?? modelFormatForNpm(npm),
    npm,
    apiUrl: model.apiUrl ?? baseUrl,
  };
}

function normalizeTemplateOverlay(
  template: ProviderTemplate,
  model: NonNullable<ProviderTemplate['staticModels']>[number],
): NonNullable<ProviderTemplate['staticModels']>[number] {
  const npm = model.npm ?? template.npm;
  const { id } = normalizeGoogleModelId(model.id, npm);
  const family = model.family;
  const hasFreeMetadata = model.cost !== undefined
    || model.isFree !== undefined
    || model.freeStatus !== undefined;
  const freeStatus = hasFreeMetadata
    ? model.freeStatus ?? classifyFreeStatus({ model })
    : undefined;

  return {
    ...model,
    id,
    name: normalizeGoogleDisplayName(model.name, id),
    ...(model.upstreamModelId !== undefined
      ? { upstreamModelId: normalizeGoogleModelId(model.upstreamModelId, npm).upstreamModelId }
      : {}),
    ...(model.npm !== undefined ? { npm } : {}),
    ...(model.modelFormat !== undefined
      ? { modelFormat: model.modelFormat }
      : model.npm !== undefined
        ? { modelFormat: modelFormatForNpm(npm) }
        : {}),
    ...(family !== undefined ? { family, brand: model.brand ?? deriveBrand(family) } : {}),
    ...(freeStatus !== undefined
      ? {
          freeStatus,
          isFree: model.isFree ?? isFreeStatus(freeStatus),
        }
      : {}),
  };
}

/**
 * Layer curated per-model metadata over a provider's live model list.
 *
 * Mixed-protocol providers expose one discovery endpoint but require different
 * SDK packages and base URLs per model. `allowlist` also provides a fail-closed
 * way to exclude protocols the runtime intentionally does not support.
 */
export function applyTemplateModelMetadata(
  template: ProviderTemplate,
  discovered: CachedModel[],
): CachedModel[] {
  const curated = new Map(
    (template.staticModels ?? [])
      .map(model => normalizeTemplateOverlay(template, model))
      .map(model => [model.id, model] as const),
  );

  const visible = template.staticModelPolicy === 'allowlist'
    ? discovered.filter(model => curated.has(model.id))
    : discovered;

  return visible.map(model => {
    const overlay = curated.get(model.id);
    if (!overlay) return model;
    return {
      ...model,
      ...overlay,
      id: model.id,
      upstreamModelId: overlay.upstreamModelId ?? model.upstreamModelId,
    };
  });
}

export interface FetchTemplateModelsResult {
  models: CachedModel[];
  baseUrl: string;
  error?: string;
  hint?: string;
}

/** Probe provider API with API key; returns models on success. */
export async function fetchTemplateModels(
  template: ProviderTemplate,
  apiKey: string,
  baseUrlOverride?: string,
  extraHeaders?: Record<string, string>,
): Promise<FetchTemplateModelsResult> {
  const trimmedOverride = baseUrlOverride?.trim();
  const baseUrl = (trimmedOverride || template.defaultBaseUrl)?.replace(/\/$/, '');
  if (!baseUrl) {
    return {
      models: [],
      baseUrl: '',
      error: 'This provider needs a base URL.',
    };
  }

  // OpenCode Go's endpoints are the reviewed package/destination pairs in the
  // retained identity authority, and this call puts a live credential on the
  // wire. `verifyCredential` is
  // already structurally unredirectable because it takes no URL; discovery
  // took one, and `refreshApiListProvider` feeds the provider record's stored
  // `api.url` straight back in here, so a registry that was hand-edited or
  // imported with a different address was enough to re-aim the key. Refuse
  // rather than quietly substitute the default: a caller that believed it had
  // redirected discovery should find out that it had not.
  if (template.id === OPENCODE_GO_PROVIDER_ID) {
    const pinned = openCodeGoPinnedApiUrl(template.npm);
    if (!pinned) {
      return {
        models: [],
        baseUrl: '',
        error: `${template.name} does not support the ${template.npm || '(missing)'} SDK package.`,
      };
    }
    if (baseUrl !== pinned) {
      return {
        models: [],
        baseUrl: '',
        error: `${template.name} does not support a custom API base URL.`,
        hint: 'This provider always uses its own endpoint. Remove the custom URL and try again.',
      };
    }
  }

  // No template declares `static-seed` today, so this arm and
  // `materializeTemplateModel` are unreachable — kept deliberately, not
  // overlooked. `static-seed` is a member of ProviderModelSource, and deleting
  // the arm would make a template that adopts it fall through to the api-list
  // path and try to fetch a catalog it does not have.
  if (template.modelSource === 'static-seed') {
    const models = (template.staticModels ?? [])
      .map(model => materializeTemplateModel(template, model, baseUrl));
    return { models, baseUrl };
  }

  const url = modelsUrl(baseUrl, template);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TEST_TIMEOUT_MS);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const trimmedApiKey = apiKey.trim();
  if (template.npm === '@ai-sdk/anthropic') {
    if (trimmedApiKey) headers['x-api-key'] = trimmedApiKey;
    headers['anthropic-version'] = '2023-06-01';
  } else if (trimmedApiKey) {
    headers['Authorization'] = `Bearer ${trimmedApiKey}`;
  }
  if (template.headers) Object.assign(headers, template.headers);
  if (extraHeaders) Object.assign(headers, extraHeaders);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      redirect: 'manual',
      signal: controller.signal,
    });

    if (response.status >= 300 && response.status < 400) {
      return {
        models: [],
        baseUrl,
        error: 'Provider redirected the connection test.',
        hint: 'Check the base URL — redirects are blocked for security.',
      };
    }

    let logTrace: ((msg: string) => void) | undefined;
    if (process.env.CLODEX_TRACE === '1') {
      if (trimmedApiKey) registerTraceSecret(trimmedApiKey);
      logTrace = makeTraceLogger(getProviderDebugLogPath());
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      if (logTrace) {
        logTrace(`[fetchTemplateModels] HTTP ${response.status} from ${url}`);
        logTrace(`[fetchTemplateModels] Body: ${body}`);
      }
      const detail = body.slice(0, 200).trim();
      if (response.status === 401 || response.status === 403) {
        return {
          models: [],
          baseUrl,
          error: 'API key was rejected.',
          hint: template.signupUrl
            ? `Get or verify your key at ${template.signupUrl}`
            : 'Double-check the key you pasted.',
        };
      }
      return {
        models: [],
        baseUrl,
        error: `Provider returned HTTP ${response.status}.`,
        hint: detail || 'Check your API key and try again.',
      };
    }

    const rawBodyText = await response.text().catch(() => '');
    if (logTrace) {
      logTrace(`[fetchTemplateModels] HTTP ${response.status} from ${url}`);
      logTrace(`[fetchTemplateModels] Body: ${rawBodyText}`);
    }

    let json: OpenAiModelListResponse = {};
    try {
      if (rawBodyText.trim()) {
        json = JSON.parse(rawBodyText) as OpenAiModelListResponse;
      }
    } catch {
      // Failed to parse, use empty object
    }

    const discovered = parseModelList(
      json,
      template.npm,
      template.id === OPENCODE_GO_PROVIDER_ID,
    );
    const models = applyTemplateModelMetadata(template, discovered);
    if (models.length === 0) {
      // An allowlist template can end up empty for two very different reasons,
      // and "no models were returned" points at the wrong one when upstream
      // answered with a healthy list that simply shares no ids with the
      // curated catalog — an upstream rename, or a catalog gone stale.
      const filteredOut = template.staticModelPolicy === 'allowlist' && discovered.length > 0;
      return {
        models: [],
        baseUrl,
        error: filteredOut
          ? `Connected, but none of the ${discovered.length} models upstream returned are in this provider's supported list.`
          : 'Connected but no models were returned.',
        hint: filteredOut
          ? 'The upstream catalog may have renamed its models, or clodex\'s list may be out of date.'
          : 'The API key may be valid but model listing is unavailable for this provider.',
      };
    }

    return { models, baseUrl };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const timedOut = message.includes('abort') || message.includes('Abort');
    return {
      models: [],
      baseUrl,
      error: timedOut ? 'Connection timed out after 10 seconds.' : 'Could not reach the provider.',
      hint: timedOut
        ? 'Check your network or try again.'
        : 'Verify the provider is online and your API key is correct.',
    };
  } finally {
    clearTimeout(timer);
  }
}
